import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { AbilityForm, ItemData, ItemRarity, ItemStatus, ItemUseConfiguration, ReferenceData } from '../../shared/types';
import { ApiRequestError, api } from '../api';
import { GameplayOutcomeEditor } from '../components/GameplayOutcomeEditor';
import { isUnrealCompatibleItemIcon, optimizeItemIcon, type OptimizedItemIcon } from '../lib/item-icon';
import { slugify, statusLabel } from '../utils';

type Draft = {
  technicalKey: string;
  status: ItemStatus;
  rarity: ItemRarity;
  category: string;
  subcategory: string;
  origins: string[];
  tags: string[];
  buyPrice: number;
  sellPrice: number;
  maximumStack: number;
  useConfiguration: ItemUseConfiguration | null;
  translations: {
    de: { name: string; description: string };
    en: { name: string; description: string };
  };
};

type KeyState = {
  status: 'idle' | 'checking' | 'available' | 'adjusted' | 'error';
  message: string;
};

const ITEM_ICON_PROMPT_PRESET_NAME = 'MYY Item Icon';

function buildItemIconPrompt(name: string, description: string) {
  const safeName = name.trim() || '{NAME}';
  const safeDescription = description.trim() || '{DESCRIPTION}';

  return [
    'Create a single stylized fantasy item icon for a game.',
    '',
    `Item name: "${safeName}"`,
    `Item description: "${safeDescription}"`,
    '',
    'Depict only the item itself as the clear main subject.',
    'The item should be centered, isolated, and fully visible.',
    'Use a transparent background.',
    'Output as a 512x512 icon.',
    'No text, no labels, no watermark, no frame, no background scene, no character holding the item.',
    '',
    'Visual style:',
    'stylized hand-painted fantasy, warm painterly rendering, whimsical magical bureaucracy aesthetic, cozy fantasy game art, readable shapes, slightly exaggerated details, ornate brass-gold accents, subtle raspberry and fig-purple design language, elegant medieval-fantasy craftsmanship, polished game icon presentation, clean silhouette, high readability, not photorealistic.',
    '',
    'Important:',
    '',
    'The icon must be suitable as a clean in-game inventory item.',
  ].join('\n');
}

function generatedKey(value: string) {
  const key = slugify(value);
  return key.length === 1 ? `${key}-item` : key;
}

const itemUseForms:{value:Exclude<AbilityForm,'passive'>;label:string}[]=[
  {value:'hitscan_target',label:'Sofort am Ziel'},{value:'self',label:'Am Anwender'},{value:'projectile',label:'Projektil'},
  {value:'beam',label:'Strahl'},{value:'persistent_area',label:'Verbleibende Fläche'},{value:'aoe_target',label:'AoE am Ziel'},{value:'aoe_self',label:'AoE am Anwender'},
];
const itemUsesRange=(form:Exclude<AbilityForm,'passive'>,origin:'target'|'self'|null)=>['hitscan_target','projectile','beam','aoe_target'].includes(form)||(form==='persistent_area'&&origin==='target');
const itemUsesRadius=(form:Exclude<AbilityForm,'passive'>)=>['persistent_area','aoe_target','aoe_self'].includes(form);
function blankItemUse(reference:ReferenceData):ItemUseConfiguration {
  return {
    form:'aoe_target',domainKey:reference.domains[0]?.key??'domain.ice',maximumRange:1500,
    rangePresetKey:reference.rangePresets.find((preset)=>preset.technicalKey==='range.medium')?.technicalKey??reference.rangePresets[0]?.technicalKey??'range.custom',customMaximumRange:null,
    resolvedRadius:100,radiusPresetKey:reference.radiusPresets.find((preset)=>preset.technicalKey==='radius.small')?.technicalKey??reference.radiusPresets[0]?.technicalKey??'radius.custom',customRadius:null,
    areaOrigin:'target',areaDurationSeconds:null,outcomes:[],
  };
}

function blank(reference: ReferenceData): Draft {
  const cat = reference.categories[0];
  return {
    technicalKey: '',
    status: 'draft',
    rarity: reference.rarities[0]?.key ?? 'common',
    category: cat?.key ?? '',
    subcategory: cat?.subcategories[0]?.key ?? '',
    origins: [],
    tags: [],
    buyPrice: 0,
    sellPrice: 0,
    maximumStack: 1,
    useConfiguration: null,
    translations: {
      de: { name: '', description: '' },
      en: { name: '', description: '' },
    },
  };
}

function fromItem(item: ItemData): Draft {
  return {
    technicalKey: item.technicalKey,
    status: item.status,
    rarity: item.rarity,
    category: item.category,
    subcategory: item.subcategory,
    origins: item.origins,
    tags: item.tags,
    buyPrice: item.buyPrice,
    sellPrice: item.sellPrice,
    maximumStack: item.maximumStack,
    useConfiguration: item.useConfiguration ? { ...item.useConfiguration, outcomes: item.useConfiguration.outcomes.map((outcome) => ({ ...outcome })) } : null,
    translations: item.translations,
  };
}

export function ItemEditorPage({
  id,
  duplicateFromId,
  reference,
  onBack,
  onDuplicate,
  onSaved,
  onDeleted,
  onMessage,
}: {
  id: string | null;
  duplicateFromId: string | null;
  reference: ReferenceData;
  onBack: () => void;
  onDuplicate: (id: string) => void;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onMessage: (kind: 'success' | 'error', message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => blank(reference));
  const [item, setItem] = useState<ItemData | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<ItemData | null>(null);
  const [loading, setLoading] = useState(Boolean(id || duplicateFromId));
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string,string>>({});
  const [activeTab, setActiveTab] = useState<'de' | 'en'>('de');
  const [activeTagGroupKey, setActiveTagGroupKey] = useState(reference.tagGroups[0]?.key ?? '');
  const [tagSearch, setTagSearch] = useState('');
  const [file, setFile] = useState<OptimizedItemIcon | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [keyState, setKeyState] = useState<KeyState>(
    id
      ? { status: 'available', message: 'Der technische Schlüssel ist nach dem Anlegen unveränderlich.' }
      : duplicateFromId
        ? { status: 'idle', message: 'Wird erst beim Speichern des Duplikats vergeben.' }
        : { status: 'idle', message: 'Wird automatisch aus dem deutschen Namen erzeugt.' },
  );
  const fileRef = useRef<HTMLInputElement>(null);
  const automaticIconRepairRef = useRef<string | null>(null);
  const fieldError = (field: string) => fieldErrors[field] ?? Object.entries(fieldErrors).find(([key]) => key.startsWith(`${field}.`))?.[1] ?? null;
  const itemUseOutcomeFieldErrors = Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([key]) => key.startsWith('useConfiguration.outcomes.'))
      .map(([key, value]) => [key.replace(/^useConfiguration\./, ''), value]),
  );
  const markFieldError = (field: string, message: string): never => {
    setFieldErrors({ [field]: message });
    throw new Error(message);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .item(id)
      .then((r) => {
        setItem(r.item);
        setDraft(fromItem(r.item));
        setFieldErrors({});
        setKeyState({ status: 'available', message: 'Der technische Schlüssel ist nach dem Anlegen unveränderlich.' });
      })
      .catch((e) => onMessage('error', e instanceof Error ? e.message : 'Item konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!duplicateFromId || id) return;
    setLoading(true);
    api
      .item(duplicateFromId)
      .then((response) => {
        setDuplicateSource(response.item);
        setItem(null);
        setDraft({ ...fromItem(response.item), technicalKey: '' });
        setFieldErrors({});
        setKeyState({ status: 'idle', message: 'Wird erst beim Speichern des Duplikats vergeben.' });
      })
      .catch((error) => onMessage('error', error instanceof Error ? error.message : 'Das Ausgangsitem konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [duplicateFromId, id]);

  useEffect(() => {
    if (id || duplicateFromId) return;

    const baseKey = generatedKey(draft.translations.de.name);
    if (!baseKey) {
      setDraft((d) => (d.technicalKey ? { ...d, technicalKey: '' } : d));
      setKeyState(
        draft.translations.de.name.trim()
          ? {
              status: 'error',
              message: 'Aus diesem Namen kann kein technischer Schlüssel erzeugt werden. Verwende mindestens einen Buchstaben oder eine Zahl.',
            }
          : { status: 'idle', message: 'Wird automatisch aus dem deutschen Namen erzeugt.' },
      );
      return;
    }

    setDraft((d) => (d.technicalKey === baseKey ? d : { ...d, technicalKey: baseKey }));
    setKeyState({ status: 'checking', message: 'Technischer Schlüssel wird in der Datenbank geprüft …' });

    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .checkTechnicalKey(baseKey)
        .then((result) => {
          if (cancelled) return;
          setDraft((d) => ({ ...d, technicalKey: result.suggestedKey }));
          setKeyState(
            result.available
              ? { status: 'available', message: 'Technischer Schlüssel ist verfügbar.' }
              : {
                  status: 'adjusted',
                  message: `„${baseKey}“ ist bereits vergeben. Automatisch verwendet: „${result.suggestedKey}“.`,
                },
          );
        })
        .catch((error) => {
          if (cancelled) return;
          setKeyState({ status: 'error', message: error instanceof Error ? error.message : 'Technischer Schlüssel konnte nicht geprüft werden.' });
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [id, duplicateFromId, draft.translations.de.name]);

  const category = reference.categories.find((c) => c.key === draft.category);
  const filePreview = useMemo(() => (file ? URL.createObjectURL(file.icon) : null), [file]);
  const promptText = useMemo(
    () => buildItemIconPrompt(draft.translations.de.name, draft.translations.de.description),
    [draft.translations.de.name, draft.translations.de.description],
  );

  useEffect(() => () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
  }, [filePreview]);

  const sourceIconItem = item ?? duplicateSource;
  const preview = filePreview ?? (sourceIconItem?.icon ? `${sourceIconItem.icon.downloadUrl}?v=${sourceIconItem.revision}` : null);
  const existingIconNeedsUnrealRepair = Boolean(sourceIconItem?.icon && !isUnrealCompatibleItemIcon(sourceIconItem.icon.contentType));
  const rarity = reference.rarities.find((entry) => entry.key === draft.rarity) ?? reference.rarities[0];
  const rarityStyle = { '--rarity-color': rarity?.color ?? '#9AA0A6' } as CSSProperties;
  const activeTagGroup = reference.tagGroups.find((group) => group.key === activeTagGroupKey) ?? reference.tagGroups[0];
  const visibleTags = activeTagGroup?.tags.filter((tag) => tag.label.toLocaleLowerCase('de-DE').includes(tagSearch.trim().toLocaleLowerCase('de-DE'))) ?? [];
  const deletionLocked = item ? ['delete_requested', 'delete_error', 'deleted'].includes(item.unrealSync.state) : false;

  useEffect(() => {
    if (!item?.icon || isUnrealCompatibleItemIcon(item.icon.contentType) || deletionLocked) return;

    const repairKey = `${item.id}:${item.icon.id}`;
    if (automaticIconRepairRef.current === repairKey) return;
    automaticIconRepairRef.current = repairKey;
    void repairStoredItemIcon(item, true);
  }, [item?.id, item?.icon?.id, item?.icon?.contentType, deletionLocked]);

  function patch<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function setName(value: string) {
    setDraft((d) => ({
      ...d,
      translations: { ...d.translations, de: { ...d.translations.de, name: value } },
    }));
  }

  function toggle(array: 'origins' | 'tags', value: string) {
    setDraft((d) => {
      const removing = d[array].includes(value);
      return {
        ...d,
        [array]: removing ? d[array].filter((entry) => entry !== value) : [...d[array], value],
        ...(array === 'tags' && value === 'nutzung.benutzbar' ? { useConfiguration: removing ? null : (d.useConfiguration ?? blankItemUse(reference)) } : {}),
      };
    });
  }

  function changeItemUseForm(form: Exclude<AbilityForm, 'passive'>) {
    setDraft((current) => {
      const use = current.useConfiguration;
      if (!use) return current;
      return {
        ...current,
        useConfiguration: {
          ...use,
          form,
          areaOrigin: form === 'persistent_area' ? (use.areaOrigin ?? 'target') : form === 'aoe_target' ? 'target' : form === 'aoe_self' ? 'self' : null,
          areaDurationSeconds: form === 'persistent_area' ? (use.areaDurationSeconds ?? 15) : null,
          resolvedRadius: itemUsesRadius(form) ? (use.resolvedRadius || 100) : 0,
          radiusPresetKey: itemUsesRadius(form) ? (use.radiusPresetKey ?? reference.radiusPresets[0]?.technicalKey ?? 'radius.custom') : null,
        },
      };
    });
  }

  function changeCategory(value: string) {
    const cat = reference.categories.find((c) => c.key === value);
    setDraft((d) => ({
      ...d,
      category: value,
      subcategory: cat?.subcategories[0]?.key ?? '',
    }));
  }


  async function prepareIcon(selected: File) {
    setImageBusy(true);
    try {
      const optimized = await optimizeItemIcon(selected);
      setFile(optimized);
      onMessage(
        'success',
        `Icon automatisch optimiert: ${Math.round(optimized.originalBytes / 1024)} KB → ${Math.round(optimized.optimizedBytes / 1024)} KB.`,
      );
    } catch (error) {
      setFile(null);
      onMessage('error', error instanceof Error ? error.message : 'Das Icon konnte nicht optimiert werden.');
    } finally {
      setImageBusy(false);
    }
  }

  async function loadOptimizedStoredIcon(source: ItemData): Promise<OptimizedItemIcon> {
    if (!source.icon) throw new Error('Für dieses Item ist kein Icon gespeichert.');

    const response = await fetch(source.icon.downloadUrl, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Das vorhandene Icon konnte nicht geladen werden.');
    const blob = await response.blob();
    const existingFile = new File([blob], source.icon.filename || 'item-icon', {
      type: blob.type || source.icon.contentType,
      lastModified: Date.now(),
    });
    return optimizeItemIcon(existingFile);
  }

  async function repairStoredItemIcon(source: ItemData, automatic = false) {
    if (!source.icon) return;
    setImageBusy(true);
    try {
      const optimized = await loadOptimizedStoredIcon(source);
      const saved = (await api.uploadIcon(source.id, optimized.icon, optimized.thumbnail)).item;
      setItem(saved);
      setDraft(fromItem(saved));
      setFieldErrors({});
      setFile(null);
      onMessage(
        'success',
        automatic
          ? 'Das ältere WebP-Icon wurde automatisch in ein Unreal-kompatibles PNG umgewandelt.'
          : 'Das Icon wurde als Unreal-kompatibles PNG gespeichert.',
      );
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Das vorhandene Icon konnte nicht repariert werden.');
    } finally {
      setImageBusy(false);
    }
  }

  async function optimizeExistingIcon() {
    if (!sourceIconItem?.icon) return;
    if (item?.id === sourceIconItem.id && !isUnrealCompatibleItemIcon(sourceIconItem.icon.contentType)) {
      await repairStoredItemIcon(sourceIconItem);
      return;
    }

    setImageBusy(true);
    try {
      const optimized = await loadOptimizedStoredIcon(sourceIconItem);
      setFile(optimized);
      onMessage('success', `Vorhandenes Icon vorbereitet: ${Math.round(optimized.originalBytes / 1024)} KB → ${Math.round(optimized.optimizedBytes / 1024)} KB.`);
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Das vorhandene Icon konnte nicht optimiert werden.');
    } finally {
      setImageBusy(false);
    }
  }

  async function copyPrompt() {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Die Zwischenablage wird von diesem Browser nicht unterstützt.');
      }
      await navigator.clipboard.writeText(promptText);
      onMessage('success', 'Der Bildgenerierungs-Prompt wurde in die Zwischenablage kopiert.');
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Der Prompt konnte nicht kopiert werden.');
    }
  }

  async function save() {
    setBusy(true);
    setFieldErrors({});
    try {
      if (!draft.translations.de.name.trim()) markFieldError('translations.de.name','Der deutsche Name ist erforderlich.');
      if (!duplicateFromId && !draft.technicalKey) markFieldError('technicalKey','Der technische Schlüssel konnte noch nicht erzeugt werden.');
      if (!id && !duplicateFromId && (keyState.status === 'checking' || keyState.status === 'error' || keyState.status === 'idle')) {
        markFieldError('technicalKey','Bitte warte, bis der technische Schlüssel geprüft wurde.');
      }
      if (!draft.translations.de.description.trim()) markFieldError('translations.de.description','Die deutsche Beschreibung ist erforderlich.');
      const missingTagGroups = reference.tagGroups.filter((group) => !group.tags.some((tag) => draft.tags.includes(tag.key)));
      if (missingTagGroups.length) {
        markFieldError('tags',`Bitte wähle in jedem Tag-Reiter mindestens einen Eintrag aus. Offen: ${missingTagGroups.map((group) => group.label).join(', ')}.`);
      }
      if (draft.status === 'approved' && draft.origins.length === 0) markFieldError('origins','Fürs Spiel freigegebene Items benötigen mindestens eine Herkunft.');
      if (imageBusy) throw new Error('Bitte warte, bis das Icon fertig optimiert wurde.');

      let finalKey = draft.technicalKey;
      if (!id) {
        const availability = await api.checkTechnicalKey(generatedKey(draft.translations.de.name));
        finalKey = availability.suggestedKey;
        if (finalKey !== draft.technicalKey && !duplicateFromId) {
          setDraft((d) => ({ ...d, technicalKey: finalKey }));
          setKeyState({ status: 'adjusted', message: `Der Schlüssel wurde unmittelbar vor dem Speichern auf „${finalKey}“ angepasst.` });
        }
      }

      let iconToUpload = file;
      if (!iconToUpload && sourceIconItem?.icon && !isUnrealCompatibleItemIcon(sourceIconItem.icon.contentType)) {
        iconToUpload = await loadOptimizedStoredIcon(sourceIconItem);
      }

      const payload = {
        ...draft,
        technicalKey: finalKey,
        translations: {
          de: {
            name: draft.translations.de.name.trim(),
            description: draft.translations.de.description.trim(),
          },
          en: {
            name: draft.translations.en.name.trim(),
            description: draft.translations.en.description.trim(),
          },
        },
        ...(duplicateFromId && !iconToUpload ? { duplicateSourceId: duplicateFromId } : {}),
      };

      const result = id ? await api.updateItem(id, payload as never) : await api.createItem(payload as never);
      let saved = result.item;
      if (iconToUpload) saved = (await api.uploadIcon(saved.id, iconToUpload.icon, iconToUpload.thumbnail)).item;

      setItem(saved);
      setDraft(fromItem(saved));
      setFieldErrors({});
      setFile(null);
      setKeyState({ status: 'available', message: 'Der technische Schlüssel ist nach dem Anlegen unveränderlich.' });
      onSaved(saved.id);
      onMessage('success', id ? 'Item wurde gespeichert.' : duplicateFromId ? 'Das Item wurde als neues Item gespeichert.' : 'Item wurde angelegt.');
    } catch (e) {
      if (e instanceof ApiRequestError) setFieldErrors(Object.fromEntries(e.fieldErrors.map((entry) => [entry.field, entry.message])));
      onMessage('error', e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (!item) return;
    setBusy(true);
    try {
      const result = item.status === 'archived' ? await api.restoreItem(item.id) : await api.archiveItem(item.id);
      setItem(result.item);
      setDraft(fromItem(result.item));
      onMessage('success', result.item.status === 'archived' ? 'Item wurde archiviert.' : 'Item wurde wiederhergestellt.');
    } catch (e) {
      onMessage('error', e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function requestDeletion() {
    if (!item) return;
    const retry = item.unrealSync.state === 'delete_error';
    const confirmed = window.confirm(
      retry
        ? 'Die Ingame-Löschung wird erneut angefordert. Das Item bleibt bis zur erfolgreichen Bestätigung gesperrt. Fortfahren?'
        : 'Das Item wird im Content Studio gesperrt und als Löschauftrag an die Content Bridge übergeben. Erst nach erfolgreicher Ingame-Löschung kann es endgültig aus dem Studio entfernt werden. Fortfahren?',
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const result = await api.requestItemDeletion(item.id);
      setItem(result.item);
      setDraft(fromItem(result.item));
      onMessage('success', result.item.unrealSync.state === 'deleted'
        ? 'Das Item war nicht Ingame vorhanden und kann nun endgültig gelöscht werden.'
        : 'Die Ingame-Löschung wurde angefordert.');
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Die Löschung konnte nicht angefordert werden.');
    } finally {
      setBusy(false);
    }
  }

  async function finalizeDeletion() {
    if (!item || item.unrealSync.state !== 'deleted') return;
    if (!window.confirm('Das Item wurde Ingame bereits gelöscht. Jetzt wird es endgültig aus dem Content Studio entfernt. Dieser Schritt kann nicht rückgängig gemacht werden. Fortfahren?')) return;
    setBusy(true);
    try {
      await api.finalizeItemDeletion(item.id);
      onMessage('success', 'Das Item wurde endgültig gelöscht. Der technische Schlüssel bleibt dauerhaft gesperrt.');
      onDeleted();
    } catch (error) {
      onMessage('error', error instanceof Error ? error.message : 'Das Item konnte nicht endgültig gelöscht werden.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="empty">Item wird geladen …</div>;

  const saveDisabled = busy || imageBusy || deletionLocked || (!id && !duplicateFromId && keyState.status !== 'available' && keyState.status !== 'adjusted');

  return (
    <>
      <div className="page-heading">
        <div>
          <button className="back-link" onClick={onBack}>← Zurück zu Items</button>
          <h1>{id ? 'Item bearbeiten' : duplicateFromId ? 'Item duplizieren' : 'Neues Item'}</h1>
          <p>{id ? draft.technicalKey : duplicateFromId ? 'Entwurf aus vorhandenem Item – ID und technischer Schlüssel entstehen erst beim Speichern.' : 'Lege einen neuen Gegenstand für den zentralen Katalog an.'}</p>
        </div>
        <div className="heading-actions">
          {item && !deletionLocked && <button className="button secondary" onClick={() => onDuplicate(item.id)}>Duplizieren</button>}
          {item && !deletionLocked && (
            <button className="button danger-ghost" onClick={archive} disabled={busy}>
              {item.status === 'archived' ? 'Wiederherstellen' : 'Archivieren'}
            </button>
          )}
          {item && !deletionLocked && <button className="button danger" onClick={() => void requestDeletion()} disabled={busy}>Löschen</button>}
          {item?.unrealSync.state === 'delete_error' && <button className="button danger" onClick={() => void requestDeletion()} disabled={busy}>Löschung erneut anfordern</button>}
          {item?.unrealSync.state === 'delete_requested' && <button className="button secondary" disabled>Ingame-Löschung ausstehend</button>}
          {item?.unrealSync.state === 'deleted' && <button className="button danger" onClick={() => void finalizeDeletion()} disabled={busy}>Endgültig löschen</button>}
          {!deletionLocked && <button className="button primary" onClick={save} disabled={saveDisabled}>
            {busy ? 'Speichert …' : 'Speichern'}
          </button>}
        </div>
      </div>

      {item && (
        <section className={`unreal-sync-banner ${item.unrealSync.state}`}>
          <div>
            <strong>
              {item.unrealSync.state === 'synced' ? '✓ Ingame-Daten sind aktuell'
                : item.unrealSync.state === 'update_required' ? '↻ Ingame-Aktualisierung ausstehend'
                  : item.unrealSync.state === 'error' ? '✕ Ingame-Übernahme fehlgeschlagen'
                    : item.unrealSync.state === 'delete_requested' ? '⌛ Ingame-Löschung ausstehend'
                      : item.unrealSync.state === 'delete_error' ? '✕ Ingame-Löschung fehlgeschlagen'
                        : item.unrealSync.state === 'deleted' ? '✓ Ingame gelöscht'
                          : '○ Noch nicht ins Spiel übernommen'}
            </strong>
            <span>
              {item.unrealSync.state === 'synced' ? `Revision ${item.unrealSync.importedRevision} wurde erfolgreich als ${item.unrealSync.assetPath ?? 'Ingame-Asset'} bestätigt.`
                : item.unrealSync.state === 'update_required' ? `Im Spiel liegt Revision ${item.unrealSync.importedRevision}; im Content Studio ist Revision ${item.revision} freigegeben.`
                  : item.unrealSync.state === 'error' ? (item.unrealSync.errorMessage ?? 'Die Ingame-Übernahme hat einen Fehler zurückgemeldet.')
                    : item.unrealSync.state === 'delete_requested' ? 'Die Content Bridge muss das Asset sicher aus dem Unreal-Projekt entfernen und den Erfolg zurückmelden.'
                      : item.unrealSync.state === 'delete_error' ? (item.unrealSync.errorMessage ?? 'Das Ingame-Asset konnte nicht gelöscht werden. Prüfe vorhandene Referenzen.')
                        : item.unrealSync.state === 'deleted' ? 'Die Ingame-Löschung wurde bestätigt. Das Item kann jetzt endgültig aus dem Content Studio entfernt werden.'
                          : 'Nach der Freigabe kann die Bridge dieses Item ins Spiel übernehmen.'}
            </span>
          </div>
          {item.unrealSync.lastAttemptAt && (
            <small>Letzter Ingame-Versuch: {new Date(item.unrealSync.lastAttemptAt).toLocaleString('de-DE')}</small>
          )}
        </section>
      )}

      {Object.keys(fieldErrors).length > 0 && <section className="validation-panel error"><strong>Bitte prüfe die markierten Felder.</strong><ul>{Object.values(fieldErrors).map((message,index)=><li key={`${message}-${index}`}>{message}</li>)}</ul></section>}

      <div className="editor-grid">
        <section className={`panel form-panel ${deletionLocked ? 'locked' : ''}`} aria-disabled={deletionLocked}>
          <div className="section-title">
            <h2>Grunddaten</h2>
            <span className={`status ${draft.status}`}>{statusLabel(draft.status)}</span>
          </div>

          <div className="form-grid three">
            <label>
              Technischer Schlüssel
              <input aria-invalid={Boolean(fieldError('technicalKey'))} className="generated-key" value={draft.technicalKey} readOnly aria-readonly="true" placeholder={duplicateFromId ? 'Wird beim Speichern vergeben' : 'Wird aus dem Namen erzeugt'} />
              <small className={`key-status ${keyState.status}`}>
                {keyState.status === 'available' && '✓ '}
                {keyState.status === 'adjusted' && '✓ '}
                {keyState.status === 'error' && '✕ '}
                {keyState.message}
              </small>
              {fieldError('technicalKey') && <small className="field-error">{fieldError('technicalKey')}</small>}
            </label>
            <label>
              Status
              <select value={draft.status} onChange={(e) => patch('status', e.target.value as ItemStatus)}>
                {reference.statuses.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
            <label>
              Seltenheit
              <select value={draft.rarity} onChange={(e) => patch('rarity', e.target.value as ItemRarity)}>
                {reference.rarities.map((entry) => (
                  <option key={entry.key} value={entry.key}>{entry.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-grid two">
            <label>
              Kategorie
              <select aria-invalid={Boolean(fieldError('category'))} value={draft.category} onChange={(e) => changeCategory(e.target.value)}>
                {reference.categories.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </label>
            <label>
              Unterkategorie
              <select aria-invalid={Boolean(fieldError('subcategory'))} value={draft.subcategory} onChange={(e) => patch('subcategory', e.target.value)}>
                {category?.subcategories.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="tabs">
            <button className={activeTab === 'de' ? 'active' : ''} onClick={() => setActiveTab('de')}>Deutsch · Pflicht</button>
            <button className={activeTab === 'en' ? 'active' : ''} onClick={() => setActiveTab('en')}>English · Optional</button>
          </div>

          {activeTab === 'de' ? (
            <div className="form-stack">
              <label>
                Name
                <input aria-invalid={Boolean(fieldError('translations.de.name'))} maxLength={50} value={draft.translations.de.name} onChange={(e) => setName(e.target.value)} />
                {fieldError('translations.de.name') && <small className="field-error">{fieldError('translations.de.name')}</small>}
                <small>{draft.translations.de.name.length}/50</small>
              </label>
              <label>
                Beschreibung
                <textarea
                  aria-invalid={Boolean(fieldError('translations.de.description'))}
                  maxLength={250}
                  value={draft.translations.de.description}
                  onChange={(e) => setDraft((d) => ({
                    ...d,
                    translations: { ...d.translations, de: { ...d.translations.de, description: e.target.value } },
                  }))}
                />
                {fieldError('translations.de.description') && <small className="field-error">{fieldError('translations.de.description')}</small>}
                <small>{draft.translations.de.description.length}/250</small>
              </label>
            </div>
          ) : (
            <div className="form-stack">
              <label>
                Name
                <input
                  maxLength={50}
                  value={draft.translations.en.name}
                  onChange={(e) => setDraft((d) => ({
                    ...d,
                    translations: { ...d.translations, en: { ...d.translations.en, name: e.target.value } },
                  }))}
                />
                <small>{draft.translations.en.name.length}/50</small>
              </label>
              <label>
                Description
                <textarea
                  maxLength={250}
                  value={draft.translations.en.description}
                  onChange={(e) => setDraft((d) => ({
                    ...d,
                    translations: { ...d.translations, en: { ...d.translations.en, description: e.target.value } },
                  }))}
                />
                <small>{draft.translations.en.description.length}/250</small>
              </label>
            </div>
          )}

          <h2 className="subheading">Werte</h2>
          <div className="form-grid three">
            <label>
              Einkaufspreis
              <input aria-invalid={Boolean(fieldError('buyPrice'))} type="number" min="0" value={draft.buyPrice} onChange={(e) => patch('buyPrice', Number(e.target.value))} />
            </label>
            <label>
              Verkaufspreis
              <input aria-invalid={Boolean(fieldError('sellPrice'))} type="number" min="0" value={draft.sellPrice} onChange={(e) => patch('sellPrice', Number(e.target.value))} />
            </label>
            <label>
              Maximaler Stapel
              <input aria-invalid={Boolean(fieldError('maximumStack'))} type="number" min="1" value={draft.maximumStack} onChange={(e) => patch('maximumStack', Number(e.target.value))} />
            </label>
          </div>

          <h2 className="subheading">Herkunft</h2>
          <div className="chip-list">
            {reference.origins.map((o) => (
              <label className={`chip ${draft.origins.includes(o.key) ? 'selected' : ''}`} key={o.key}>
                <input type="checkbox" checked={draft.origins.includes(o.key)} onChange={() => toggle('origins', o.key)} />
                {o.label}
              </label>
            ))}
          </div>

          <h2 className="subheading">Tags</h2>
          <div className="compact-tag-editor">
            <div className="tag-group-tabs" role="tablist" aria-label="Tag-Gruppen">
              {reference.tagGroups.map((group) => {
                const selectedCount = group.tags.filter((tag) => draft.tags.includes(tag.key)).length;
                return <button
                  type="button"
                  role="tab"
                  aria-selected={activeTagGroup?.key === group.key}
                  className={`tag-group-tab ${activeTagGroup?.key === group.key ? 'active' : ''} ${selectedCount ? 'complete' : 'missing'}`}
                  key={group.key}
                  onClick={() => { setActiveTagGroupKey(group.key); setTagSearch(''); }}
                >
                  <span>{group.label}</span><strong>{selectedCount || '!'}</strong>
                </button>;
              })}
            </div>
            {activeTagGroup && <div className="tag-picker-panel" role="tabpanel">
              <div className="tag-picker-heading">
                <div><strong>{activeTagGroup.label}</strong><span>Mindestens eine Auswahl ist Pflicht.</span></div>
                <input value={tagSearch} onChange={(event) => setTagSearch(event.target.value)} placeholder="Tags durchsuchen …" />
              </div>
              <div className="chip-list tag-picker-list">
                {visibleTags.map((tag) => (
                  <label className={`chip ${draft.tags.includes(tag.key) ? 'selected' : ''}`} key={tag.key}>
                    <input type="checkbox" checked={draft.tags.includes(tag.key)} onChange={() => toggle('tags', tag.key)} />
                    {tag.label}
                  </label>
                ))}
                {visibleTags.length === 0 && <span className="tag-picker-empty">Keine passenden Tags in diesem Reiter.</span>}
              </div>
            </div>}
          </div>

          {draft.tags.includes('nutzung.benutzbar') && draft.useConfiguration && <>
            <div className="section-heading use-config-heading">
              <div><h2>Bei Benutzung</h2><p>Das Item beschreibt sein Gameplay direkt. Animationen, Niagara und Sounds werden danach ausschließlich am Item-DataAsset in Unreal ergänzt.</p></div>
            </div>
            <div className="ability-form-grid compact-forms">
              {itemUseForms.map((option)=><button type="button" className={`ability-form-card ${draft.useConfiguration?.form===option.value?'selected':''}`} key={option.value} onClick={()=>changeItemUseForm(option.value)}><strong>{option.label}</strong></button>)}
            </div>
            <div className="form-grid three">
              <label className={fieldError('useConfiguration.domainKey') ? 'field-invalid' : undefined}>Domäne<select aria-invalid={Boolean(fieldError('useConfiguration.domainKey'))} value={draft.useConfiguration.domainKey} onChange={(event)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,domainKey:event.target.value}}:current)}>{reference.domains.map((domain)=><option key={domain.key} value={domain.key}>{domain.label}</option>)}</select>{fieldError('useConfiguration.domainKey')&&<span className="field-error">{fieldError('useConfiguration.domainKey')}</span>}</label>
              {itemUsesRange(draft.useConfiguration.form,draft.useConfiguration.areaOrigin)&&<label className={fieldError('useConfiguration.rangePresetKey') ? 'field-invalid' : undefined}>Reichweitenklasse<select aria-invalid={Boolean(fieldError('useConfiguration.rangePresetKey'))} value={draft.useConfiguration.rangePresetKey??'range.custom'} onChange={(event)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,rangePresetKey:event.target.value}}:current)}>{reference.rangePresets.map((preset)=><option key={preset.technicalKey} value={preset.technicalKey}>{preset.displayName} · {preset.valueCm} cm</option>)}<option value="range.custom">Sonderfall / individuell</option></select>{fieldError('useConfiguration.rangePresetKey')&&<span className="field-error">{fieldError('useConfiguration.rangePresetKey')}</span>}</label>}
              {itemUsesRadius(draft.useConfiguration.form)&&<label className={fieldError('useConfiguration.radiusPresetKey') ? 'field-invalid' : undefined}>Radiusklasse<select aria-invalid={Boolean(fieldError('useConfiguration.radiusPresetKey'))} value={draft.useConfiguration.radiusPresetKey??'radius.custom'} onChange={(event)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,radiusPresetKey:event.target.value}}:current)}>{reference.radiusPresets.map((preset)=><option key={preset.technicalKey} value={preset.technicalKey}>{preset.displayName} · {preset.valueCm} cm</option>)}<option value="radius.custom">Sonderfall / individuell</option></select>{fieldError('useConfiguration.radiusPresetKey')&&<span className="field-error">{fieldError('useConfiguration.radiusPresetKey')}</span>}</label>}
            </div>
            <div className="form-grid three">
              {draft.useConfiguration.rangePresetKey==='range.custom'&&itemUsesRange(draft.useConfiguration.form,draft.useConfiguration.areaOrigin)&&<label className={fieldError('useConfiguration.customMaximumRange') ? 'field-invalid' : undefined}>Individuelle Reichweite (cm)<input aria-invalid={Boolean(fieldError('useConfiguration.customMaximumRange'))} type="number" min="0" value={draft.useConfiguration.customMaximumRange??draft.useConfiguration.maximumRange} onChange={(event)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,customMaximumRange:Number(event.target.value)}}:current)}/>{fieldError('useConfiguration.customMaximumRange')&&<span className="field-error">{fieldError('useConfiguration.customMaximumRange')}</span>}</label>}
              {draft.useConfiguration.radiusPresetKey==='radius.custom'&&itemUsesRadius(draft.useConfiguration.form)&&<label className={fieldError('useConfiguration.customRadius') ? 'field-invalid' : undefined}>Individueller Radius (cm)<input aria-invalid={Boolean(fieldError('useConfiguration.customRadius'))} type="number" min="0" value={draft.useConfiguration.customRadius??draft.useConfiguration.resolvedRadius} onChange={(event)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,customRadius:Number(event.target.value)}}:current)}/>{fieldError('useConfiguration.customRadius')&&<span className="field-error">{fieldError('useConfiguration.customRadius')}</span>}</label>}
              {draft.useConfiguration.form==='persistent_area'&&<label className={fieldError('useConfiguration.areaDurationSeconds') ? 'field-invalid' : undefined}>Dauer der Fläche (s)<input aria-invalid={Boolean(fieldError('useConfiguration.areaDurationSeconds'))} type="number" min="0.1" step="0.1" value={draft.useConfiguration.areaDurationSeconds??15} onChange={(event)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,areaDurationSeconds:Number(event.target.value)}}:current)}/>{fieldError('useConfiguration.areaDurationSeconds')&&<span className="field-error">{fieldError('useConfiguration.areaDurationSeconds')}</span>}</label>}
            </div>
            {draft.useConfiguration.form==='persistent_area'&&<div className="segmented-choice"><button type="button" className={draft.useConfiguration.areaOrigin==='target'?'active':''} onClick={()=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,areaOrigin:'target'}}:current)}>Am Ziel</button><button type="button" className={draft.useConfiguration.areaOrigin==='self'?'active':''} onClick={()=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,areaOrigin:'self'}}:current)}>Am Anwender</button></div>}
            <div className="section-heading"><div><h3>Wirkungen</h3><p>Beispiel Kryophiole: Eis + Weltinteraktion „Löschen“ + feste Stärke 100. Schaden ist nicht erforderlich.</p></div></div>
            <GameplayOutcomeEditor reference={reference} outcomes={draft.useConfiguration.outcomes} fieldErrors={itemUseOutcomeFieldErrors} onChange={(outcomes)=>setDraft((current)=>current.useConfiguration?{...current,useConfiguration:{...current.useConfiguration,outcomes}}:current)}/>
          </>}
        </section>

        <div className="editor-aside">
          <aside className="panel icon-panel">
            <div className="icon-panel-heading"><h2>Item-Icon</h2><span className="rarity-label" style={rarityStyle}>{rarity?.label ?? draft.rarity}</span></div>
            <div className={`icon-preview rarity-frame rarity-${draft.rarity}`} style={rarityStyle}>
              {preview ? <img src={preview} /> : <div><img src="/assets/potion.svg" /><span>Noch kein Icon</span></div>}
            </div>
            <input
              ref={fileRef}
              hidden
              type="file"
              accept="image/png,image/webp"
              onChange={(event) => {
                const selected = event.target.files?.[0] ?? null;
                event.currentTarget.value = '';
                if (selected) void prepareIcon(selected);
              }}
            />
            <button className="button secondary block" disabled={imageBusy} onClick={() => fileRef.current?.click()}>
              {imageBusy ? 'Icon wird optimiert …' : 'PNG oder WebP auswählen'}
            </button>
            {sourceIconItem?.icon && !file && (
              <button className="button ghost block" disabled={imageBusy} onClick={() => void optimizeExistingIcon()}>
                {existingIconNeedsUnrealRepair ? 'Icon jetzt als PNG reparieren' : 'Vorhandenes Icon optimieren'}
              </button>
            )}
            <p>Das Content Studio erzeugt für Unreal ein kompatibles 512 × 512-PNG und zusätzlich ein schnelles Listen-Thumbnail. Das Original wird nicht gespeichert.</p>
            {file && (
              <div className="file-info optimized-file-info">
                <strong>{file.originalName}</strong>
                <span>Original: {file.originalWidth} × {file.originalHeight} · {Math.round(file.originalBytes / 1024)} KB</span>
                <span>Ingame-Datei: 512 × 512 PNG · {Math.round(file.optimizedBytes / 1024)} KB</span>
                <span>Listen-Thumbnail: 128 × 128 · {Math.round(file.thumbnailBytes / 1024)} KB</span>
                <span>{file.hasTransparency ? '✓ Transparenz erkannt' : '⚠ Das Ausgangsbild besitzt keinen transparenten Bereich'}</span>
              </div>
            )}
            {duplicateSource && !item && (
              <div className="metadata duplicate-metadata">
                <div><span>Duplikat von</span><strong>{duplicateSource.translations.de.name}</strong></div>
                <div><span>Neue Cloud-ID</span><strong>Wird erst beim Speichern erzeugt</strong></div>
                <div><span>Neuer technischer Schlüssel</span><strong>Wird erst beim Speichern erzeugt</strong></div>
              </div>
            )}
            {item && (
              <div className="metadata">
                <div><span>Cloud-ID</span><code>{item.id}</code></div>
                <div><span>Revision</span><strong>{item.revision}</strong></div>
                <div><span>Ingame-Revision</span><strong>{item.unrealSync.importedRevision || '–'}</strong></div>
                <div><span>Ingame-Asset</span><code>{item.unrealSync.assetPath ?? 'Noch nicht erfasst'}</code></div>
              </div>
            )}
          </aside>

          <aside className="panel prompt-panel prompt-panel-compact">
            <div className="prompt-compact-copy">
              <div>
                <span className="eyebrow">{ITEM_ICON_PROMPT_PRESET_NAME}</span>
                <h2>Bildprompt</h2>
                <p>Wird automatisch aus Name und Beschreibung erzeugt.</p>
              </div>
              <button className="button secondary" onClick={copyPrompt}>Prompt kopieren</button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
